/**
 * Database health & retention — the firewall's storage guard
 * (services/firewall/src/services/dbHousekeeping).
 *
 * Shows size against budget, disk free, the guard level (ok / warn / aggressive /
 * critical = shedding log payloads), per-table sizes and the last purge runs.
 * Retention windows are editable here; they override the deployment's env
 * defaults and are admin-audited server-side.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from './ui';
import { usePermission } from '../hooks/usePermission';
import { useTranslation } from 'react-i18next';

interface Rule {
  table: string; days: number | null; maxRows: number | null; cls: string; minDays: number;
  source: 'default' | 'env' | 'admin'; description: string; partitioned?: boolean; chain?: unknown;
}
interface TableStat { table: string; rows: number; bytes: number; deadRows?: number }
interface Run { id: number; started_at: string; finished_at: string | null; kind: string; level: string; deleted_rows: number; dropped_partitions: number }
interface Health {
  driver: 'postgres' | 'sqlite'; measuredAt: string; dbBytes: number; budgetBytes: number; budgetSource: string;
  usagePct: number; disk: { path: string; totalBytes: number; freeBytes: number; freePct: number } | null;
  level: 'ok' | 'warn' | 'aggressive' | 'critical'; reasons: string[]; shedding: boolean;
  tables: TableStat[]; retention: Rule[]; recentRuns: Run[];
  server?: { version: string; connections: number; maxConnections: number };
}

const json = { 'Content-Type': 'application/json' };
const getLEVEL = (t: (key: string) => string): Record<Health['level'], { label: string; color: string }> => ({
  ok: { label: t('components.dbHealthPanel.healthy'), color: 'var(--success, #1f9d55)' },
  warn: { label: t('components.dbHealthPanel.warning'), color: 'var(--warning, #d69e2e)' },
  aggressive: { label: t('components.dbHealthPanel.purgingAggressively'), color: 'var(--warning, #dd6b20)' },
  critical: { label: t('components.dbHealthPanel.criticalSheddingLogPayloads'), color: 'var(--danger, #e53e3e)' },
});

function fmtBytes(b: number): string {
  if (!b) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(u.length - 1, Math.floor(Math.log(b) / Math.log(1024)));
  return `${(b / 1024 ** i).toFixed(i ? 1 : 0)} ${u[i]}`;
}

const DbHealthPanel: React.FC<{ apiUrl: string }> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const { t: tr } = useTranslation();
  const canWrite = usePermission('manage:settings');
  const [h, setH] = useState<Health | null>(null);
  const [auditDays, setAuditDays] = useState('');
  const [telemetryDays, setTelemetryDays] = useState('');
  const [loaded, setLoaded] = useState({ audit: '', telemetry: '' });
  const [tableEdits, setTableEdits] = useState<Record<string, string>>({});
  const [showTables, setShowTables] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/admin/system/db-health`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d: Health = await r.json();
      setH(d);
      const audit = d.retention.find((x) => x.table === 'audit_logs');
      const tele = d.retention.find((x) => x.table === 'a2a_events');
      const a = audit?.days != null ? String(audit.days) : '';
      const t = tele?.days != null ? String(tele.days) : '';
      setAuditDays(a); setTelemetryDays(t); setLoaded({ audit: a, telemetry: t });
      setTableEdits({});
    } catch (e: any) {
      setMsg({ kind: 'error', text: `Could not load database health: ${e.message}` });
    }
  }, [apiUrl]);

  useEffect(() => { void load(); }, [load]);

  const call = async (fn: () => Promise<void>, ok: string) => {
    setBusy(true); setMsg(null);
    try { await fn(); setMsg({ kind: 'ok', text: ok }); await load(); }
    catch (e: any) { setMsg({ kind: 'error', text: e.message }); }
    finally { setBusy(false); }
  };

  const save = () => call(async () => {
    const body: any = {};
    // only what changed, so untouched windows keep following the deployment's env defaults
    if (auditDays !== '' && auditDays !== loaded.audit) body.auditDays = Number(auditDays);
    if (telemetryDays !== '' && telemetryDays !== loaded.telemetry) body.telemetryDays = Number(telemetryDays);
    const tables: Record<string, { days: number | null }> = {};
    for (const [t, v] of Object.entries(tableEdits)) tables[t] = { days: v === '' ? null : Number(v) };
    if (Object.keys(tables).length) body.tables = tables;
    if (!Object.keys(body).length) return;
    const r = await fetch(`${apiUrl}/admin/system/retention`, { method: 'PUT', headers: json, body: JSON.stringify(body) });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `Save failed: HTTP ${r.status}`);
  }, 'Retention saved. The next hourly purge applies it (or run one now).');

  const purgeNow = () => call(async () => {
    const r = await fetch(`${apiUrl}/admin/system/housekeeping/run`, { method: 'POST' });
    if (!r.ok) throw new Error(`Purge failed: HTTP ${r.status}`);
  }, 'Purge finished.');

  if (!h) {
    return msg ? <div className="notice error"><Icon name="warning" /> {msg.text}</div> : <div style={{ fontSize: 13 }}>{tr('components.dbHealthPanel.loading')}</div>;
  }

  const lv = getLEVEL(t)[h.level] || getLEVEL(t).ok;
  const pct = Math.min(100, h.usagePct);
  const dis = !canWrite || busy;
  const lbl: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, marginTop: 12 };
  const sizes = new Map(h.tables.map((t) => [t.table, t]));
  const topTables = [...h.tables].sort((a, b) => b.bytes - a.bytes).slice(0, 8);

  return (
    <>
      {msg && (
        <div className={`notice ${msg.kind === 'error' ? 'error' : ''}`}>
          <Icon name={msg.kind === 'error' ? 'warning' : 'checkCircle'} /> {msg.text}
        </div>
      )}
      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">
          {t('components.dbHealthPanel.storageTitle', { driver: h.driver === 'postgres' ? 'PostgreSQL' : 'SQLite' })}{h.server ? ` ${h.server.version}` : ''}
          <span style={{ marginLeft: 10, fontWeight: 600, color: lv.color }}>● {lv.label}</span>
        </div>
        <div className="ent-card-body">
          <div style={{ fontSize: 13 }}>
            {t('components.dbHealthPanel.usageOfBudget', { used: fmtBytes(h.dbBytes), budget: fmtBytes(h.budgetBytes), pct: h.usagePct.toFixed(1) })}
            <span style={{ color: 'var(--text-2)' }}> {t('components.dbHealthPanel.budgetFrom', { source: h.budgetSource === 'env' ? 'AGENTI5_DB_MAX_GB' : h.budgetSource === 'volume' ? t('components.dbHealthPanel.pct70OfVolume') : t('components.dbHealthPanel.builtInDefault') })}</span>
          </div>
          <div style={{ height: 8, background: 'var(--border, #e2e8f0)', borderRadius: 4, marginTop: 6, maxWidth: 520 }}>
            <div style={{ width: `${pct}%`, height: '100%', borderRadius: 4, background: lv.color }} />
          </div>
          {h.disk && (
            <div style={{ fontSize: 13, marginTop: 8 }}>{tr('components.dbHealthPanel.diskFreeOf', { v0: h.disk.path, v1: fmtBytes(h.disk.freeBytes), v2: fmtBytes(h.disk.totalBytes), v3: h.disk.freePct.toFixed(1) })}</div>
          )}
          {h.server && <div style={{ fontSize: 13, marginTop: 4 }}>{tr('components.dbHealthPanel.connections', { v0: h.server.connections, v1: h.server.maxConnections })}</div>}
          {h.reasons.length > 0 && <div style={{ fontSize: 12, marginTop: 6, color: lv.color }}>{h.reasons.join('; ')}</div>}
          {h.shedding && (
            <div style={{ fontSize: 12, marginTop: 6 }}>
              {tr('components.dbHealthPanel.policyEnforcementContinuesAuditRowsKeep')}
            </div>
          )}
          <table style={{ width: '100%', maxWidth: 620, fontSize: 12, marginTop: 12 }}>
            <thead><tr><th align="left">{tr('components.dbHealthPanel.largestTables')}</th><th align="right">{tr('components.dbHealthPanel.rows')}</th><th align="right">{tr('components.dbHealthPanel.size')}</th></tr></thead>
            <tbody>
              {topTables.map((t) => (
                <tr key={t.table}><td>{t.table}</td><td align="right">{t.rows.toLocaleString()}</td><td align="right">{fmtBytes(t.bytes)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">{tr('components.dbHealthPanel.retention')}</div>
        <div className="ent-card-body">
          <label style={lbl}>{tr('components.dbHealthPanel.auditLogsAuditLogsAdminAudit')}</label>
          <input className="ent-input" type="number" min={1} style={{ maxWidth: 120 }} value={auditDays} disabled={dis}
            onChange={(e) => setAuditDays(e.target.value)} />
          <label style={lbl}>{tr('components.dbHealthPanel.telemetryA2AEventsAnomaliesLLMUsage')}</label>
          <input className="ent-input" type="number" min={1} style={{ maxWidth: 120 }} value={telemetryDays} disabled={dis}
            onChange={(e) => setTelemetryDays(e.target.value)} />
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 8 }}>
            {tr('components.dbHealthPanel.purgedRowsOfHashChainedTables')}
          </div>

          <button className="ent-btn" style={{ marginTop: 12, background: 'transparent', color: 'var(--text)' }} onClick={() => setShowTables(!showTables)}>
            {t(showTables ? 'components.dbHealthPanel.hide' : 'components.dbHealthPanel.show')} {t('components.dbHealthPanel.perTableRules')}
          </button>
          {showTables && (
            <table style={{ width: '100%', fontSize: 12, marginTop: 8 }}>
              <thead><tr><th align="left">{tr('components.dbHealthPanel.table')}</th><th align="left">{tr('components.dbHealthPanel.rule')}</th><th align="right">{tr('components.dbHealthPanel.rows')}</th><th align="left">{tr('components.dbHealthPanel.days')}</th><th align="left">{tr('components.dbHealthPanel.source')}</th></tr></thead>
              <tbody>
                {h.retention.map((r) => (
                  <tr key={r.table}>
                    <td>{r.table}{r.partitioned && h.driver === 'postgres' ? ' ⧉' : ''}</td>
                    <td style={{ color: 'var(--text-2)' }}>{r.description}{r.maxRows ? ` (cap ${r.maxRows.toLocaleString()} rows)` : ''}</td>
                    <td align="right">{sizes.get(r.table)?.rows.toLocaleString() ?? '—'}</td>
                    <td>
                      <input className="ent-input" type="number" min={r.minDays} style={{ width: 80 }} disabled={dis}
                        value={tableEdits[r.table] ?? (r.days ?? '')}
                        onChange={(e) => setTableEdits({ ...tableEdits, [r.table]: e.target.value })} />
                    </td>
                    <td>{r.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {canWrite ? (
            <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
              <button className="ent-btn" disabled={busy} onClick={save}>{busy ? tr('components.dbHealthPanel.working') : tr('components.dbHealthPanel.saveRetention')}</button>
              <button className="ent-btn" disabled={busy} onClick={purgeNow}>{tr('components.dbHealthPanel.runPurgeNow')}</button>
            </div>
          ) : (
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-2)' }}>{tr('components.dbHealthPanel.readOnlyYouLackTheManage')}</div>
          )}
        </div>
      </div>

      <div className="ent-card">
        <div className="ent-card-head">{tr('components.dbHealthPanel.recentPurgeRuns')}</div>
        <div className="ent-card-body" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 12 }}>
            <thead><tr><th align="left">{tr('components.dbHealthPanel.started')}</th><th align="left">{tr('components.dbHealthPanel.kind')}</th><th align="left">{tr('components.dbHealthPanel.level')}</th><th align="right">{tr('components.dbHealthPanel.rowsRemoved')}</th><th align="right">{tr('components.dbHealthPanel.partitionsDropped')}</th></tr></thead>
            <tbody>
              {h.recentRuns.map((r) => (
                <tr key={r.id}><td>{String(r.started_at).replace('T', ' ').slice(0, 19)}</td><td>{r.kind}</td><td>{r.level}</td>
                  <td align="right">{r.finished_at ? Number(r.deleted_rows).toLocaleString() : tr('components.dbHealthPanel.running')}</td><td align="right">{r.dropped_partitions}</td></tr>
              ))}
              {h.recentRuns.length === 0 && <tr><td colSpan={5} style={{ color: 'var(--text-2)' }}>{tr('components.dbHealthPanel.noPurgeHasRunYetFirst')}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default DbHealthPanel;
